import { Button } from "@/components/ui/button";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view.ts";
import { useShouldShowFloatingChat } from "@/lib/hooks/admin/use-should-show-floating-chat.ts";
import { useActiveCallNotification } from "@/lib/hooks/conversations/useActiveCallNotification.tsx";
import { usePopupStore } from "@/lib/hooks/conversations/use-popup-store.ts";
import { useChannelsList } from "@/lib/hooks/conversations/use-channels-list";
import { useNewMentorUI } from "@/lib/hooks/use-new-mentor-ui";
import { INTERNAL_OPS_CUSTOM_TYPES } from "@/lib/constants";
import { ChatBubbleIcon } from "@radix-ui/react-icons";
import { useLocation } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useMemo } from "react";
import { ChatPopup } from "./chat-popup.tsx";
import { useAuth } from "../auth/auth-context.tsx";
import { UserRole } from "@/lib/api/user.ts";

export const FloatingChat = () => {
  const { user } = useAuth();
  const shouldShowFloatingChat = useShouldShowFloatingChat();
  const isNewMentorUI = useNewMentorUI();

  const mentorView = useMentorView((state) => state.mentorView);
  const { pathname } = useLocation();

  // New mentor shell pins Support in Notice; hide competing FAB there.
  // The legacy Support page already has the full channel list.
  const hideFab =
    pathname === "/support" ||
    pathname.startsWith("/support/") ||
    (isNewMentorUI &&
      (pathname === "/conversations" ||
        pathname.startsWith("/conversations/")));

  const isEscalationTriggerVisible =
    !hideFab &&
    ((pathname === "/conversations" &&
      (user?.role === UserRole.MENTOR ? true : mentorView)) ||
      pathname?.includes("mentorships"));

  const { chatPopupOpen, escalationPopupOpen, setChatPopupOpen } =
    usePopupStore();

  useActiveCallNotification();

  const { channels: opsChannels, loading: opsChannelsLoading } =
    useChannelsList({
      customTypesFilter: [...INTERNAL_OPS_CUSTOM_TYPES],
      enabled: shouldShowFloatingChat && !hideFab,
      enableNotifications: false,
    });

  const hasUnreadOpsMessages = useMemo(
    () => opsChannels.some((c) => c.unreadMessageCount > 0),
    [opsChannels],
  );

  const handleClick = () => {
    setChatPopupOpen(!chatPopupOpen);
  };

  if (!shouldShowFloatingChat) return null;
  if (hideFab) return null;

  return (
    <div
      className="fixed bottom-4 z-50"
      style={{
        transition: "right 0.3s ease-in-out",
        right:
          escalationPopupOpen && chatPopupOpen
            ? "32.5rem"
            : isEscalationTriggerVisible
              ? "5rem"
              : "1rem",
      }}
    >
      {chatPopupOpen ? (
        <ChatPopup
          channels={opsChannels}
          channelsLoading={opsChannelsLoading}
          onClose={() => setChatPopupOpen(false)}
          isOpen={chatPopupOpen}
          position={"right"}
          isEscalationTriggerVisible={isEscalationTriggerVisible}
        />
      ) : null}

      <div className={`relative ${!chatPopupOpen && "cursor-move"}`}>
        {hasUnreadOpsMessages && !chatPopupOpen && (
          <div className="absolute left-[2px] top-[2px] h-3 w-3 rounded-full bg-red-500" />
        )}
        <Button
          onClick={handleClick}
          size="icon"
          className="h-14 w-14 rounded-full bg-white text-slate-900 shadow-lg hover:bg-slate-100"
        >
          {chatPopupOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <ChatBubbleIcon className="h-6 w-6" />
          )}
        </Button>
      </div>
    </div>
  );
};
